import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  BarChart3, Eye, MousePointerClick, Share2, ShoppingBag, TrendingUp,
  Lock, ArrowRight, Crown, Loader2,
} from "lucide-react";
import { API } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";

const RANGE_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

const KPI_DEFS = [
  { key: "profile_views", label: "Profile Views", icon: Eye, color: "text-tbc-gold" },
  { key: "product_views", label: "Product Views", icon: Eye, color: "text-tbc-gold" },
  { key: "contact_clicks", label: "Contact Clicks", icon: MousePointerClick, color: "text-emerald-500" },
  { key: "share_clicks", label: "Shares", icon: Share2, color: "text-blue-500" },
  { key: "cta_clicks", label: "CTA Clicks", icon: TrendingUp, color: "text-purple-500" },
  { key: "orders", label: "Orders", icon: ShoppingBag, color: "text-amber-500" },
];

function Sparkbars({ data, max }) {
  if (!data?.length) return null;
  return (
    <div className="flex items-end gap-[2px] h-20 mt-3" data-testid="analytics-sparkbars">
      {data.map((d, i) => {
        const pct = max > 0 ? (d.total / max) * 100 : 0;
        return (
          <div
            key={d.date}
            className="flex-1 bg-tbc-gold/30 rounded-t hover:bg-tbc-gold transition-colors relative group"
            style={{ height: `${Math.max(pct, 2)}%` }}
            title={`${d.date}: ${d.total} views`}
          >
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-tbc-navy text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
              {d.date} · {d.total}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsCard({ business }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tier = business?.subscription_tier || "free";
  const isPremium = tier === "premium" || tier === "founding";

  useEffect(() => {
    if (!business?.business_id || !isPremium) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/analytics/business/${business.business_id}/summary?days=${days}`,
          { withCredentials: true }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [business?.business_id, days, isPremium]);

  // ---- Locked card for free tier ----
  if (!isPremium) {
    return (
      <div
        data-testid="analytics-locked"
        className="relative overflow-hidden rounded-2xl bg-white border border-tbc-navy/10 p-6 mb-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-tbc-navy/5 flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-tbc-navy/40" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs tracking-[0.2em] uppercase text-tbc-navy/50 font-medium">Analytics</p>
              <span className="text-[10px] bg-tbc-gold text-tbc-navy px-2 py-0.5 rounded-full font-bold">PREMIUM</span>
            </div>
            <h3 className="font-heading text-xl text-tbc-navy">See who&apos;s visiting your profile</h3>
            <p className="text-sm text-tbc-navy/65 mt-1">
              Track profile views, product views, contact clicks, and conversion rate — all in one dashboard.
            </p>
          </div>
          <Link to="/pricing" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy px-4 py-2 text-sm font-semibold whitespace-nowrap">
            Unlock <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Faux preview */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 opacity-50 pointer-events-none select-none">
          {[
            { label: "Profile Views", val: "1,284" },
            { label: "Product Views", val: "3,412" },
            { label: "Contact Clicks", val: "127" },
            { label: "Conversion", val: "3.2%" },
          ].map((s) => (
            <div key={s.label} className="bg-tbc-navy/5 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-tbc-navy/50">{s.label}</p>
              <p className="font-heading text-xl text-tbc-navy mt-1">{s.val}</p>
            </div>
          ))}
        </div>

        <Link
          to="/pricing"
          data-testid="analytics-unlock-cta"
          className="sm:hidden mt-5 inline-flex items-center gap-2 rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy px-4 py-2 text-sm font-semibold"
        >
          Upgrade to Unlock <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  // ---- Premium dashboard ----
  const totals = data?.totals || {};
  const maxDaily = data?.timeseries ? Math.max(1, ...data.timeseries.map(d => d.total)) : 0;

  return (
    <div data-testid="analytics-card" className="rounded-2xl bg-white border border-tbc-navy/10 p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-tbc-gold/15 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-tbc-gold" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs tracking-[0.2em] uppercase text-tbc-navy/60 font-medium">Analytics</p>
              {tier === "founding" && (
                <span className="inline-flex items-center gap-1 text-[10px] bg-gradient-to-r from-tbc-gold to-amber-400 text-tbc-navy px-2 py-0.5 rounded-full font-bold">
                  <Crown className="w-3 h-3" /> FOUNDING
                </span>
              )}
            </div>
            <h3 className="font-heading text-xl text-tbc-navy">Performance overview</h3>
          </div>
        </div>

        <div className="inline-flex bg-tbc-navy/5 rounded-full p-1" data-testid="analytics-range">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              data-testid={`analytics-range-${r.value}`}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${days === r.value ? "bg-tbc-navy text-white" : "text-tbc-navy/60 hover:text-tbc-navy"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-tbc-navy/50 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
        </div>
      )}
      {error && !loading && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {data && !loading && (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {KPI_DEFS.map(({ key, label, icon: Icon, color }) => (
              <div key={key} className="bg-tbc-cream/60 rounded-lg p-3" data-testid={`kpi-${key}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider text-tbc-navy/50">{label}</p>
                  <Icon className={`w-3.5 h-3.5 ${color}`} />
                </div>
                <p className="font-heading text-2xl text-tbc-navy">
                  {key === "conversion_rate_pct"
                    ? `${totals.conversion_rate_pct ?? 0}%`
                    : (totals[key] ?? 0).toLocaleString()}
                </p>
              </div>
            ))}
          </div>

          {/* Conversion rate strip */}
          <div className="mt-4 flex items-center justify-between text-sm bg-tbc-navy text-white rounded-lg px-4 py-3">
            <span className="text-white/70">Conversion rate</span>
            <span className="font-heading text-xl text-tbc-gold" data-testid="kpi-conversion">
              {totals.conversion_rate_pct ?? 0}%
            </span>
          </div>

          {/* Daily views chart */}
          <div className="mt-6">
            <p className="text-sm font-medium text-tbc-navy/80 mb-1">Daily views</p>
            <p className="text-xs text-tbc-navy/50">Profile + product views, last {days} days</p>
            <Sparkbars data={data.timeseries} max={maxDaily} />
            <div className="flex justify-between text-[10px] text-tbc-navy/40 mt-1">
              <span>{data.timeseries?.[0]?.date}</span>
              <span>{data.timeseries?.[data.timeseries.length - 1]?.date}</span>
            </div>
          </div>

          {/* Top products */}
          <div className="mt-6">
            <p className="text-sm font-medium text-tbc-navy/80 mb-3">Top products by views</p>
            {data.top_products?.length ? (
              <ul className="space-y-2" data-testid="top-products">
                {data.top_products.map((p, idx) => (
                  <li key={p.product_id} className="flex items-center gap-3 bg-tbc-cream/60 rounded-lg p-2.5">
                    <span className="w-6 h-6 rounded-full bg-tbc-navy text-tbc-gold text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    {p.image_url ? (
                      <img src={resolveAssetUrl(p.image_url)} alt="" className="w-10 h-10 rounded-md object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-tbc-navy/10" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-tbc-navy truncate">{p.name}</p>
                      {p.price != null && <p className="text-xs text-tbc-navy/50">${p.price.toFixed(2)}</p>}
                    </div>
                    <span className="text-sm font-semibold text-tbc-navy whitespace-nowrap">
                      {p.views.toLocaleString()} views
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-tbc-navy/50 bg-tbc-cream/60 rounded-lg p-4 text-center">
                No product views yet. Share your listing to start collecting data.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
